/**
 * RFQ request rounds — the commit path behind Files → RFQs → "Request quotes".
 *
 * One request = one envelope (label, Concept / Production, due date, message) that
 * goes to each supplier as a single RFQ: one `rfqPackages` doc per supplier whose
 * snapshot holds one node set per structure they quote (`hubFileReleaseIds` lists the
 * staged release behind each). The supplier gets one email with one combined CSV,
 * one portal entry, and uploads one quote (optionally tagged per structure). The
 * chosen timber-list versions are staged first when nobody has staged them yet; the
 * `rfqRequests` doc is written last so `onRfqRequestSent` emails after packages exist.
 */

import { collection, deleteField, doc, onSnapshot, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import type {
  ProposalNodeSet,
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  RfqRequest,
  RfqRequestLine,
  RfqRequestStage,
  SupplierShipToAddress,
  SupplierShipToPreset,
  TimberSupplierInvite,
  TimberSupplierStructureRef,
} from '../types';
import { db } from './firebase';
import { publishLinkFieldsFromHubRelease } from './proposalRfqVersionLink';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import { getRfqHubFileRelease, releaseProjectFileToRfqStaging } from './rfqHubFileReleaseService';
import { loadStagedTimberListNodeSets } from './rfqHubTimberImport';
import { rfqProjectSupplierOrgIds } from './rfqProjectSuppliers';
import { structureSetKey } from './structureSelectionLabel';
import { laneKeyForStructures } from './rfqRequestCoverage';
import { packageHubReleaseIds } from './rfqPackageReleases';
import { normalizeRfqRequestStage } from './rfqRequestStage';

/** A structure lane the wizard decided to request (before staging / publishing). */
export interface RfqRequestLineDraft {
  key: string;
  structures: TimberSupplierStructureRef[];
  sourceStreamId: string;
  sourceFileId: string;
  versionIndex?: number;
  versionLabel?: string;
  /** Species on this structure's list (from the proposal); listed per structure in the email. */
  species?: string[];
  /** Supplier-facing name for the list ("Barn timber list"). */
  displayLabel: string;
  /** Cross-section inches stay as written in the source CSV. */
  trueDimensions?: boolean;
}

export type PublishRfqPackageFn = (input: {
  rfqProjectId: string;
  supplierOrgId: string;
  targetSupplierOrgIds?: string[];
  dealId: string;
  dealTitle?: string;
  workType?: 'timber_rfq';
  proposalVersionId?: string | null;
  proposalSnapshot?: { nodeSets: ProposalNodeSet[] };
  label?: string;
  releaseDisplayLabel?: string;
  timberListSource?: 'proposal_live' | 'proposal_saved_version' | 'hub_csv' | 'hub_staged_release';
  hubFileReleaseId?: string;
  hubFileReleaseIds?: string[];
  releaseVersionIndex?: number;
  linkedProposalVersionId?: string | null;
  linkedProposalVersionLabel?: string;
  linkedProposalVersionSource?: import('../types').ProposalVersionSource;
  linkedProposalVersionSavedAt?: Date;
  linkedFrom?: 'proposal_push' | 'hub_file_stage';
  customEmailMessage?: string;
  shipToByOrgId?: Record<string, SupplierShipToAddress>;
  shipToPresetByOrgId?: Record<string, SupplierShipToPreset>;
  scopeStructuresByOrgId?: Record<string, TimberSupplierStructureRef[]>;
  request?: import('../types').RfqPackageRequestEnvelope;
  supersedesPackageIdByOrgId?: Record<string, string>;
  createdByUid: string;
  createdByName: string;
}) => Promise<string[]>;

export interface SendRfqRequestDeps {
  publishRfqPackage: PublishRfqPackageFn;
  updateRfqProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  /** Stage a project file; the function finds / creates the work order when `rfqProjectId` is omitted. */
  stageRelease?: typeof releaseProjectFileToRfqStaging;
  getRelease?: typeof getRfqHubFileRelease;
  loadNodeSets?: typeof loadStagedTimberListNodeSets;
  /** Writes the `rfqRequests/{id}` doc. */
  writeRequest?: (id: string, request: RfqRequest) => Promise<void>;
  newRequestId?: () => string;
}

export interface SendRfqRequestInput {
  dealId: string;
  projectId: string;
  dealTitle?: string;
  timberProjectName?: string;
  label: string;
  stage?: RfqRequestStage;
  message?: string;
  quoteDeadline?: Date;
  lines: RfqRequestLineDraft[];
  supplierOrgIds: string[];
  /** Which line keys each supplier receives; absent org → every line. */
  lineKeysByOrg?: Record<string, string[]>;
  /** Complete invite rows for every org in `supplierOrgIds` (species, dryness, ship-to). */
  invites: TimberSupplierInvite[];
  shipToByOrgId: Record<string, SupplierShipToAddress>;
  shipToPresetByOrgId: Record<string, SupplierShipToPreset>;
  /** The deal's timber work order, when one exists. */
  existingProject: RfqProject | null;
  existingReleases: ReadonlyArray<RfqHubFileRelease>;
  /** Packages already on the deal — used for the supersedes link. */
  existingPackages: ReadonlyArray<RfqPackage>;
  proposalNodeSets?: ProposalNodeSet[] | null;
  actor: { uid: string; name: string };
}

export interface SendRfqRequestResult {
  requestId: string;
  rfqProjectId: string;
  request: RfqRequest;
  /** Lines that failed for one supplier; the rest of the round still went out. */
  failures: string[];
}

/** Default round label: "Concept timber quotes" / "Production timber quotes". */
export function defaultRfqRequestLabel(stageLabel: string | undefined): string {
  const s = (stageLabel || '').trim();
  return s ? `${s} timber quotes` : 'Timber quotes';
}

/** Lines a supplier receives under a request's per-org selection (absent org → all). */
export function linesForOrg<T extends { key: string }>(
  lines: ReadonlyArray<T>,
  orgId: string,
  lineKeysByOrg: Record<string, string[]> | undefined,
): T[] {
  const keys = lineKeysByOrg?.[orgId];
  if (!keys) return [...lines];
  const wanted = new Set(keys);
  return lines.filter(l => wanted.has(l.key));
}

/**
 * The most recent earlier package for the same supplier + structure lane — the one
 * a newer request replaces. Matched by explicit scope, then by the track the staged
 * release came from.
 */
export function previousPackageForLane(
  packages: ReadonlyArray<RfqPackage>,
  releases: ReadonlyArray<RfqHubFileRelease>,
  supplierOrgId: string,
  line: Pick<RfqRequestLineDraft, 'structures' | 'sourceStreamId'>,
  excludeIds: ReadonlySet<string> = new Set(),
): RfqPackage | null {
  const laneKey = laneKeyForStructures(line.structures);
  const relById = new Map(releases.filter(r => r.id).map(r => [r.id!, r]));
  let best: RfqPackage | null = null;
  for (const p of packages) {
    if (!p.id || excludeIds.has(p.id) || p.supplierOrgId !== supplierOrgId) continue;
    if (p.workType && p.workType !== 'timber_rfq') continue;
    const rels = packageHubReleaseIds(p).map(id => relById.get(id)).filter((r): r is RfqHubFileRelease => !!r);
    const sameTrack = rels.some(r => r.sourceStreamId === line.sourceStreamId);
    const scope = (p.scopeStructures ?? []).map(s => s.id);
    const laneIds = line.structures.map(s => s.id).filter(Boolean);
    // A combined round package covers the lane when its scope contains every structure on it.
    const sameScope = scope.length
      ? structureSetKey(scope) === laneKey || (laneIds.length > 0 && laneIds.every(id => scope.includes(id)))
      : rels.some(r => r.structureIds?.length && structureSetKey(r.structureIds) === laneKey);
    if (!sameTrack && !sameScope) continue;
    if (!best || p.createdAt.getTime() > best.createdAt.getTime()) best = p;
  }
  return best;
}

function mergeInvites(existing: TimberSupplierInvite[] | undefined, incoming: TimberSupplierInvite[]): TimberSupplierInvite[] {
  const byOrg = new Map<string, TimberSupplierInvite>((existing ?? []).map(i => [i.supplierOrgId, i]));
  for (const inv of incoming) byOrg.set(inv.supplierOrgId, { ...byOrg.get(inv.supplierOrgId), ...inv });
  return Array.from(byOrg.values()).map(inv => removeUndefinedDeep(inv) as TimberSupplierInvite);
}

export async function sendRfqRequest(input: SendRfqRequestInput, deps: SendRfqRequestDeps): Promise<SendRfqRequestResult> {
  const stageRelease = deps.stageRelease ?? releaseProjectFileToRfqStaging;
  const getRelease = deps.getRelease ?? getRfqHubFileRelease;
  const loadNodeSets = deps.loadNodeSets ?? loadStagedTimberListNodeSets;
  const writeRequest = deps.writeRequest ?? defaultWriteRequest;
  const newRequestId = deps.newRequestId ?? (() => doc(collection(db, 'rfqRequests')).id);

  const label = input.label.trim();
  if (!label) throw new Error('Give the request a label.');
  const supplierOrgIds = Array.from(new Set(input.supplierOrgIds.filter(Boolean)));
  if (!supplierOrgIds.length) throw new Error('Pick at least one supplier.');
  const lines = input.lines.filter(l => l.sourceFileId && l.sourceStreamId);
  if (!lines.length) throw new Error('Pick at least one timber list to request.');
  for (const org of supplierOrgIds) {
    if (!linesForOrg(lines, org, input.lineKeysByOrg).length) {
      throw new Error('Every supplier needs at least one structure to quote.');
    }
  }

  const failures: string[] = [];
  const requestId = newRequestId();

  // ── 1. Stage (or reuse) a release per line. The first staging call also finds or
  //       creates the deal's timber work order when there is none yet. ──
  let rfqProjectId = input.existingProject?.id ?? '';
  const releaseByLine = new Map<string, RfqHubFileRelease>();
  for (const line of lines) {
    const knownProjectId = rfqProjectId;
    const reused = input.existingReleases.find(
      r => r.sourceFileId === line.sourceFileId && (!knownProjectId || r.rfqProjectId === knownProjectId) && r.workType === 'timber_rfq',
    );
    if (reused?.id) {
      // A list staged before the box was checked still has the old flag. The email
      // reads the request line; the supplier portal reads this release.
      const marked = line.trueDimensions === true;
      let release = reused;
      if ((reused.trueDimensions === true) !== marked) {
        await updateDoc(doc(db, 'rfqProjects', reused.rfqProjectId, 'hubFileReleases', reused.id), {
          trueDimensions: marked ? true : deleteField(),
        });
        release = { ...reused, ...(marked ? { trueDimensions: true as const } : { trueDimensions: undefined }) };
      }
      releaseByLine.set(line.key, release);
      if (!rfqProjectId) rfqProjectId = reused.rfqProjectId;
      continue;
    }
    const staged = await stageRelease({
      dealId: input.dealId,
      projectFileId: line.sourceFileId,
      timberProjectId: input.projectId,
      displayLabel: line.displayLabel,
      timberProjectName: input.timberProjectName,
      dealTitle: input.dealTitle,
      rfqProjectId: rfqProjectId || undefined,
    });
    rfqProjectId = staged.rfqProjectId;
    const release = (await getRelease(rfqProjectId, staged.releaseId)) ?? {
      id: staged.releaseId,
      rfqProjectId,
      dealId: input.dealId,
      workType: 'timber_rfq' as const,
      sourceProjectId: input.projectId,
      sourceFileId: line.sourceFileId,
      sourceStreamId: line.sourceStreamId,
      sourceStreamKind: 'timber_list_csv' as const,
      sourceStreamTitle: line.displayLabel,
      structureIds: line.structures.map(s => s.id),
      structureNames: line.structures.map(s => s.name),
      versionIndex: line.versionIndex,
      versionLabel: line.versionLabel,
      displayLabel: line.displayLabel,
      releaseVersionIndex: staged.releaseVersionIndex,
      storagePath: staged.releasedStoragePath,
      releasedUrl: staged.releasedUrl,
      fileName: staged.name,
      releasedByUid: input.actor.uid,
      releasedByName: input.actor.name,
      createdAt: new Date(),
    };
    releaseByLine.set(line.key, release);
  }
  if (!rfqProjectId) throw new Error('Could not find or create the timber RFQ work order for this deal.');

  // ── 2. Make sure every supplier is on the work order with an invite (species,
  //       dryness, ship-to). publishRfqPackage refuses orgs that are not on the roster. ──
  const existing = input.existingProject && input.existingProject.id === rfqProjectId ? input.existingProject : null;
  const roster = Array.from(new Set([...(existing ? rfqProjectSupplierOrgIds(existing) : []), ...supplierOrgIds]));
  const invites = mergeInvites(existing?.timberSupplierInvites, input.invites.filter(i => supplierOrgIds.includes(i.supplierOrgId)));
  await deps.updateRfqProject(rfqProjectId, {
    supplierOrgIds: roster,
    ...(existing?.supplierOrgId ? {} : { supplierOrgId: supplierOrgIds[0] }),
    timberSupplierInvites: invites,
    workType: 'timber_rfq',
    ...(input.quoteDeadline ? { quoteDeadline: input.quoteDeadline } : {}),
  });

  // ── 3. Read each staged list once; every structure becomes one node set. ──
  const nodeSetsByLine = new Map<string, ProposalNodeSet[]>();
  const lineLoadError = new Map<string, string>();
  for (const line of lines) {
    const release = releaseByLine.get(line.key)!;
    try {
      const loaded = await loadNodeSets(rfqProjectId, release.id!, line.displayLabel, input.proposalNodeSets ?? undefined);
      nodeSetsByLine.set(line.key, loaded.nodeSets);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'could not read the staged timber list';
      lineLoadError.set(line.key, msg);
      failures.push(`${line.displayLabel}: ${msg}`);
    }
  }

  // ── 4. Publish ONE package per supplier covering every structure they quote: one
  //       email, one portal entry, one combined CSV, one quote upload. ──
  const envelopeBase = {
    rfqRequestId: requestId,
    requestLabel: label,
    requestStage: input.stage,
    requestMessage: input.message?.trim() || undefined,
    quoteDeadline: input.quoteDeadline,
  };
  const packageIdByLineAndOrg = new Map<string, Record<string, string>>(lines.map(l => [l.key, {}]));
  const createdIds = new Set<string>();
  for (const org of supplierOrgIds) {
    const orgLines = linesForOrg(lines, org, input.lineKeysByOrg).filter(l => !lineLoadError.has(l.key));
    if (!orgLines.length) continue;
    const releases = orgLines.map(l => releaseByLine.get(l.key)!);
    const nodeSets = orgLines.flatMap(l =>
      (nodeSetsByLine.get(l.key) ?? []).map(ns => ({ ...ns, rfqSourceLineKey: l.key })),
    );
    const scope = combinedLineScope(orgLines);
    const primary = releases[0];
    const link = publishLinkFieldsFromHubRelease(primary);
    const prev = newestPreviousPackageForLines(input.existingPackages, input.existingReleases, org, orgLines, createdIds);
    try {
      const ids = await deps.publishRfqPackage({
        rfqProjectId,
        supplierOrgId: org,
        targetSupplierOrgIds: [org],
        dealId: input.dealId,
        dealTitle: input.dealTitle,
        workType: 'timber_rfq',
        proposalVersionId: link.proposalVersionId,
        proposalSnapshot: nodeSets.length ? { nodeSets } : undefined,
        label,
        releaseDisplayLabel: combinedReleaseDisplayLabel(orgLines, label, primary.displayLabel),
        timberListSource: link.timberListSource,
        hubFileReleaseId: primary.id!,
        hubFileReleaseIds: releases.map(r => r.id!).filter(Boolean),
        releaseVersionIndex: primary.releaseVersionIndex,
        linkedProposalVersionId: link.proposalVersionId,
        linkedProposalVersionLabel: link.linkedProposalVersionLabel,
        linkedProposalVersionSource: link.linkedProposalVersionSource,
        linkedProposalVersionSavedAt: link.linkedProposalVersionSavedAt,
        linkedFrom: link.linkedFrom,
        customEmailMessage: input.message?.trim() || undefined,
        shipToByOrgId: input.shipToByOrgId[org] ? { [org]: input.shipToByOrgId[org] } : undefined,
        shipToPresetByOrgId: input.shipToPresetByOrgId[org] ? { [org]: input.shipToPresetByOrgId[org] } : undefined,
        scopeStructuresByOrgId: scope ? { [org]: scope } : undefined,
        request: { ...envelopeBase, rfqRequestLineKey: orgLines[0].key, rfqRequestLineKeys: orgLines.map(l => l.key) },
        supersedesPackageIdByOrgId: prev?.id ? { [org]: prev.id } : undefined,
        createdByUid: input.actor.uid,
        createdByName: input.actor.name,
      });
      if (ids[0]) {
        createdIds.add(ids[0]);
        for (const l of orgLines) packageIdByLineAndOrg.get(l.key)![org] = ids[0];
      }
    } catch (err) {
      failures.push(`${orgLines.map(l => l.displayLabel).join(', ')} → ${org}: ${err instanceof Error ? err.message : 'publish failed'}`);
    }
  }
  const outLines: RfqRequestLine[] = lines.map(line => ({
    ...lineDoc(line, releaseByLine.get(line.key)!),
    packageIdByOrg: packageIdByLineAndOrg.get(line.key) ?? {},
  }));

  // ── 5. Record the round (last, so the email trigger sees every package). ──
  const request: RfqRequest = {
    id: requestId,
    dealId: input.dealId,
    projectId: input.projectId,
    rfqProjectId,
    workType: 'timber_rfq',
    label,
    stage: input.stage,
    message: input.message?.trim() || undefined,
    quoteDeadline: input.quoteDeadline,
    sentAt: new Date(),
    sentByUid: input.actor.uid,
    sentByName: input.actor.name || undefined,
    supplierOrgIds,
    lines: outLines,
  };
  await writeRequest(requestId, request);
  return { requestId, rfqProjectId, request, failures };
}

/**
 * Union of the structures across a supplier's lines; `undefined` when any line is
 * whole-project (the package then covers the full snapshot, no scope filter).
 */
export function combinedLineScope(
  lines: ReadonlyArray<Pick<RfqRequestLineDraft, 'structures'>>,
): TimberSupplierStructureRef[] | undefined {
  if (!lines.length || lines.some(l => !l.structures.length)) return undefined;
  const byId = new Map<string, TimberSupplierStructureRef>();
  for (const l of lines) for (const s of l.structures) if (s.id && !byId.has(s.id)) byId.set(s.id, { id: s.id, name: s.name });
  return Array.from(byId.values());
}

/**
 * Supplier-facing package name: a single list keeps its own label (falling back to the
 * staged release's label), while a package spanning several lists uses the round label.
 */
export function combinedReleaseDisplayLabel(
  lines: ReadonlyArray<Pick<RfqRequestLineDraft, 'displayLabel'>>,
  roundLabel: string,
  singleLineFallback?: string,
): string {
  if (lines.length === 1) return lines[0].displayLabel || singleLineFallback || roundLabel;
  return roundLabel;
}

/** Newest earlier package for this supplier on any of these lanes — the one the combined package replaces. */
export function newestPreviousPackageForLines(
  packages: ReadonlyArray<RfqPackage>,
  releases: ReadonlyArray<RfqHubFileRelease>,
  supplierOrgId: string,
  lines: ReadonlyArray<Pick<RfqRequestLineDraft, 'structures' | 'sourceStreamId'>>,
  excludeIds: ReadonlySet<string> = new Set(),
): RfqPackage | null {
  let best: RfqPackage | null = null;
  for (const line of lines) {
    const prev = previousPackageForLane(packages, releases, supplierOrgId, line, excludeIds);
    if (prev && (!best || prev.createdAt.getTime() > best.createdAt.getTime())) best = prev;
  }
  return best;
}

function lineDoc(line: RfqRequestLineDraft, release: RfqHubFileRelease): Omit<RfqRequestLine, 'packageIdByOrg'> {
  return {
    key: line.key,
    structures: line.structures.map(s => ({ id: s.id, name: s.name })),
    sourceStreamId: line.sourceStreamId,
    sourceFileId: line.sourceFileId,
    versionIndex: line.versionIndex ?? release.versionIndex,
    versionLabel: line.versionLabel ?? release.versionLabel,
    hubFileReleaseId: release.id!,
    displayLabel: line.displayLabel || release.displayLabel,
    ...(line.trueDimensions || release.trueDimensions ? { trueDimensions: true as const } : {}),
    ...(line.species?.length ? { species: line.species } : {}),
  };
}

async function defaultWriteRequest(id: string, request: RfqRequest): Promise<void> {
  const { id: _id, sentAt, quoteDeadline, ...rest } = request;
  const payload = removeUndefinedDeep({
    ...rest,
    sentAt: Timestamp.fromDate(sentAt),
    ...(quoteDeadline instanceof Date ? { quoteDeadline: Timestamp.fromDate(quoteDeadline) } : {}),
  });
  await setDoc(doc(db, 'rfqRequests', id), payload);
}

// ── Reads ────────────────────────────────────────────────────────────────────

function requestFromDoc(id: string, data: Record<string, unknown>): RfqRequest {
  const ts = (v: unknown): Date | undefined => {
    const anyV = v as { toDate?: () => Date } | undefined;
    return anyV && typeof anyV.toDate === 'function' ? anyV.toDate() : v instanceof Date ? v : undefined;
  };
  const lines = Array.isArray(data.lines) ? (data.lines as Record<string, unknown>[]) : [];
  return {
    id,
    dealId: String(data.dealId || ''),
    projectId: String(data.projectId || ''),
    rfqProjectId: String(data.rfqProjectId || ''),
    workType: 'timber_rfq',
    label: String(data.label || ''),
    stage: normalizeRfqRequestStage(data.stage),
    message: data.message ? String(data.message) : undefined,
    quoteDeadline: ts(data.quoteDeadline),
    sentAt: ts(data.sentAt) ?? new Date(0),
    sentByUid: String(data.sentByUid || ''),
    sentByName: data.sentByName ? String(data.sentByName) : undefined,
    supplierOrgIds: Array.isArray(data.supplierOrgIds) ? (data.supplierOrgIds as string[]).filter(Boolean) : [],
    lines: lines.map(l => ({
      key: String(l.key || ''),
      structures: Array.isArray(l.structures)
        ? (l.structures as { id?: string; name?: string }[]).map(s => ({ id: String(s?.id || ''), name: String(s?.name || '') })).filter(s => s.id)
        : [],
      sourceStreamId: String(l.sourceStreamId || ''),
      sourceFileId: String(l.sourceFileId || ''),
      ...(Array.isArray(l.species) ? { species: (l.species as unknown[]).map(x => String(x ?? '').trim()).filter(Boolean) } : {}),
      versionIndex: typeof l.versionIndex === 'number' ? l.versionIndex : undefined,
      versionLabel: l.versionLabel ? String(l.versionLabel) : undefined,
      hubFileReleaseId: String(l.hubFileReleaseId || ''),
      displayLabel: String(l.displayLabel || ''),
      ...(l.trueDimensions === true ? { trueDimensions: true as const } : {}),
      packageIdByOrg: (l.packageIdByOrg && typeof l.packageIdByOrg === 'object' ? (l.packageIdByOrg as Record<string, string>) : {}),
    })),
  };
}

/** Every request round on a deal, newest first. */
export function subscribeRfqRequestsForDeal(
  dealId: string,
  onData: (requests: RfqRequest[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, 'rfqRequests'), where('dealId', '==', dealId));
  return onSnapshot(
    q,
    snap =>
      onData(
        snap.docs
          .map(d => requestFromDoc(d.id, d.data() as Record<string, unknown>))
          .sort((a, b) => b.sentAt.getTime() - a.sentAt.getTime()),
      ),
    err => onError?.(err),
  );
}
